<template>
  <div class="mt-12 flex flex-col lg:flex-row lg:gap-6">
    <div class="flex flex-col flex-1">
      <FileSelector
        v-model="showFileSelector"
        type="assets"
        subcategory="product_image"
        :extra="code"
        @fileUploaded="fileUploaded"
        @selectedFile="fileUploaded"
      />
      <CustomImagesModal
        :code="code"
        v-if="showCustomImages"
        @closed="showCustomImages = false"
      />
      <button
        v-if="images.length > 0"
        class="text-red-500 text-sm"
        @click="showCustomImages = true"
      >
        {{ $t("message.product_images.view_custom_images") }}
      </button>
      <div class="box-image flex justify-evenly">
        <baseLoading :loading="loading" />
        <template v-for="(image, i) in images">
          <div class="relative flex justify-center flex-col" :key="i">
            <img class="image" :src="image.imageRoute" alt="" />
            <button class="absolute right-2 top-2">
              <img
                @click="removeImage(image)"
                class="base-icon"
                src="@/assets/icons/trash.svg"
                alt="Delete"
              />
            </button>
            <ConfirmAction
              v-if="image.id === editingImageId"
              @confirmed="confirmDelete(image)"
              @canceled="editingImageId = null"
            />
            <button
              v-if="!image.main || image.main === '0'"
              class="rounded-full bg-gray-400 text-white px-2 mx-auto"
              @click="setImageMain(image)"
            >
              set as main
            </button>
            <p v-else class="text-green-600 text-center">
              Main image
            </p>
          </div>
        </template>
      </div>
      <button
        @click="showFileSelector = true"
        class="mx-auto mt-10 flex-col justify-center"
      >
        <img src="@/assets/icons/upload-clear.svg" alt="upload-image" />
        {{ $t("message.product_images.upload_image") }}
      </button>
      <button class="mt-20 text-red-400" @click="showImageCopier = true">
        {{ $t("message.product_images.copy_images") }}
      </button>
      <button
        class="mt-20 text-red-400 bg-red-100 px-2 py-1 rounded-full w-min whitespace-nowrap mx-auto"
        @click="$emit('generateKitImage')"
        v-if="productType === 'KIT'"
      >
        {{ $t("message.product_images.generate_kit_image") }}
      </button>
      <ProductImageCopier
        v-if="showImageCopier"
        :code="code"
        @close="showImageCopier = false"
        @imageCopied="imageCopied"
      ></ProductImageCopier>
      <ProductImagesLogs :code="code" />
    </div>

    <!-- Imágenes generadas con IA para este producto, siempre a la vista -->
    <ProductAiImages
      class="lg:w-72 mt-8 lg:mt-0"
      :code="code"
      :busy-id="addingAiImageId"
      @use-image="addAiImage"
      @use-in-description="addAiImageToDescription"
    />
  </div>
</template>
<script>
import ConfirmAction from "@/components/shared/ConfirmAction.vue";
import ProductImageCopier from "@/components/product/ProductImageCopier.vue";
import FileSelector from "@/components/FileSelector.vue";
import ProductImagesLogs from "@/components/product/ProductImagesLogs.vue";

import {
  saveImageToProduct,
  deleteImageApi,
  setMainImageApi
} from "@/api/masterAdmin/ProductImagesService.js";
import CustomImagesModal from "@/components/product/CustomImagesModal.vue";
import ProductAiImages from "@/components/product/ProductAiImages.vue";

export default {
  components: {
    ConfirmAction,
    FileSelector,
    CustomImagesModal,
    ProductAiImages,
    ProductImageCopier,
    ProductImagesLogs
  },
  props: {
    images: {
      type: Array,
      required: true
    },
    code: {
      type: String,
      required: true
    },
    productType: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showFileSelector: false,
      editingImageId: "",
      loading: false,
      showCustomImages: false,
      addingAiImageId: null,
      showImageCopier: false
    };
  },
  methods: {
    removeImage(image) {
      this.editingImageId = image.id;
    },
    confirmDelete(image) {
      this.loading = true;
      deleteImageApi(this.code, image)
        .then(res => {
          if (res.newMain) {
            this.$emit("imageSetMain", res.newMain);
          }
          this.loading = false;
          this.$emit("imageDeleted", image);
          this.$toast.open({
            message: `Image added to product`,
            type: "success",
            position: "top-right"
          });
        })
        .catch(err => {
          const message = err.response.data ?? `Error on request`;
          this.$toast.open({
            message: message,
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.loading = false;
          this.editingImageId = "";
        });
    },
    setImageMain(image) {
      console.log("set main", image);
      this.loading = true;
      setMainImageApi(this.code, image)
        .then(() => {
          this.loading = false;
          this.$emit("imageSetMain", image);
          this.$toast.open({
            message: `Image set as main`,
            type: "success",
            position: "top-right"
          });
        })
        .catch(err => {
          const message = err.response.data ?? `Error on request`;
          this.$toast.open({
            message: message,
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fileUploaded(file) {
      this.loading = true;
      this.showFileSelector = false;
      saveImageToProduct(this.code, file.s3Url)
        .then(res => {
          this.$emit("imageUploaded", res);
          this.$toast.open({
            message: `Image added to product`,
            type: "success",
            position: "top-right"
          });
        })
        .catch(err => {
          const message = err.response.data ?? `Error on request`;
          this.$toast.open({
            message: message,
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    /**
     * Adjunta una imagen generada con IA al producto. Ya está en S3
     * (IA-images/), así que se reusa el mismo alta por URL que una subida.
     */
    addAiImage(image) {
      this.addingAiImageId = image.id;
      this.loading = true;
      saveImageToProduct(this.code, image.url)
        .then(res => {
          this.$emit("imageUploaded", res);
          this.$toast.open({
            message: this.$t("message.product_images.ai_image_added"),
            type: "success",
            position: "top-right"
          });
        })
        .catch(err => {
          const message = err.response?.data ?? `Error on request`;
          this.$toast.open({
            message: message,
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.loading = false;
          this.addingAiImageId = null;
        });
    },
    addAiImageToDescription(image) {
      this.$emit("addImageToDescription", image.url);
    },
    imageCopied() {
      this.$emit("imagesUpdated");
    }
  }
};
</script>
<style lang="scss">
.box-image {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  justify-content: left;
  .image {
    max-width: 160px;
    max-height: 160px;
  }
}
</style>
